import { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import Actions from './Actions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';
import { ADMIN_PERMISSIONS } from '../../constants/adminPermissions';

const RoleTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [roles, setRoles] = useState([]);

    const fetchRoles = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/roles');
            setRoles(data.roles);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load roles", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRoles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteHandler = async (id) => {
        try {
            await axios.delete(`/api/v1/admin/role/${id}`);
            enqueueSnackbar("Role deleted", { variant: "success" });
            setRoles((prev) => prev.filter((r) => r._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete role", { variant: "error" });
        }
    };

    const columns = [
        {
            field: "name",
            headerName: "Role Name",
            minWidth: 200,
            flex: 1,
        },
        {
            field: "permissions",
            headerName: "Access",
            minWidth: 350,
            flex: 2,
            renderCell: (params) => {
                const perms = params.row.permissions || {};
                const activeModules = Object.keys(perms).filter((key) => perms[key]?.view);
                return (
                    <span className="text-sm text-gray-600">
                        {activeModules.length === 0
                            ? "No pages assigned"
                            : activeModules
                                .map((key) => ADMIN_PERMISSIONS.find((p) => p.key === key)?.label || key)
                                .join(", ")}
                    </span>
                );
            },
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 100,
            flex: 0.3,
            sortable: false,
            renderCell: (params) => (
                <Actions editRoute={"role"} deleteHandler={deleteHandler} id={params.row.id} name={params.row.name} />
            ),
        },
    ];

    const rows = roles.map((r) => ({
        id: r._id,
        name: r.name,
        permissions: r.permissions || [],
    }));

    return (
        <>
            <MetaData title="Admin: Roles | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <h1 className="text-lg font-medium uppercase">Role Management</h1>
                <Link to="/admin/role/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Role</Link>
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full" style={{ height: 470 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={10}
                    disableSelectIconOnClick
                    sx={{ boxShadow: 0, border: 0 }}
                />
            </div>
        </>
    );
};

export default RoleTable;